import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, Validators, AbstractControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ElementService } from '../../../core/services/element.service';
import { OptionService } from '../../../core/services/option.service';
import { Element } from '../../../core/models/element.model';
import { Option } from '../../../core/models/option.model';
import { ElementOptionCreateComponent } from '../element-option-create/element-option-create.component';
import { VisationModalComponent } from '../../../notifications/visation-modal/visation-modal.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-element-create',
  templateUrl: './element-create.component.html',
  styleUrls: ['./element-create.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatExpansionModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    ElementOptionCreateComponent,
  ],
})
export class ElementCreateComponent implements OnInit {
  @Input() isMaintenanceUpdate: boolean = false;
  @Input() index!: number;
  @Input() formGroup!: FormGroup;
  @Output() remove = new EventEmitter<void>();
  elements: Element[] = [];
  options: Option[] = [];

  constructor(
    private elementService: ElementService,
    private optionService: OptionService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadElements();
    this.loadOptions();
    this.initializeFormGroup();
  }

  loadElements(): void {
    this.elementService.getElements().subscribe((data: Element[]) => {
      this.elements = data;
    });
  }

  loadOptions(): void {
    this.optionService.getOptions().subscribe((data: Option[]) => {
      this.options = data;
      // console.log('Opciones cargadas:', this.options);
    });
  }

  initializeFormGroup(): void {
    if (!this.formGroup.contains('labelName')) {
      this.formGroup.addControl('labelName', this.fb.control('', Validators.required));
    }
    if (!this.formGroup.contains('elementType')) {
      this.formGroup.addControl('elementType', this.fb.control('', Validators.required));
    }
    if (!this.formGroup.contains('options')) {
      this.formGroup.addControl('options', this.fb.array([]));
    }
  }

  isComboBoxSelected(): boolean {
    const isSelected =
      this.formGroup.get('elementType')?.value === 4 ||
      this.formGroup.get('elementType')?.value === 16;
    if (isSelected && this.optionsFormArray.length === 0) {
      this.addOption();
    }
    return isSelected;
  }

  addOptionControls(): void {
    if (!this.formGroup.contains('options')) {
      this.formGroup.addControl('options', this.fb.array([]));
    }
  }

  addOption(): void {
    const optionsArray = this.optionsFormArray;
    optionsArray.push(
      this.fb.group({
        elementOptionId: [null],
        id: [null, Validators.required],
        elements: this.fb.array([]),
      }),
    );
  }

  removeOption(index: number): void {
    const optionsArray = this.optionsFormArray;
    optionsArray.removeAt(index);
  }

  get optionsFormArray(): FormArray {
    return this.formGroup.get('options') as FormArray;
  }

  getSubOptionsFormArray(optionIndex: number): FormArray {
    const optionGroup = this.optionsFormArray.at(optionIndex) as FormGroup;
    return optionGroup.get('elements') as FormArray;
  }

  getOptionFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  addNewOption(): void {
    const optionsArray = this.optionsFormArray;
    const newOptionGroup = this.fb.group({
      elementOptionId: [null], // Añadido para el nuevo ID de opción
      id: [null, Validators.required],
      elements: this.fb.array([]),
    });
    optionsArray.push(newOptionGroup);
  }

  addElementOption(control: AbstractControl): void {
    const optionIndex = this.optionsFormArray.controls.indexOf(control);
    const subOptionsArray = this.getSubOptionsFormArray(optionIndex);
    subOptionsArray.push(
      this.fb.group({
        elementOptionRelationshipId: [null],
        labelName: '',
        elementType: '',
        order: [null, Validators.required],
        required: false,
      }),
    );
  }

  removeElement() {
    this.remove.emit();
  }

  getElementTypeName(elementTypeId: number): string {
    const element = this.elements.find(e => e.id === elementTypeId);
    return element ? element.name : '';
  }

  // Abrir el modal para notificar cambios
  openVisationModal(): void {
    let visationData = {
      effectiveDate: '',
      observation: '',
      originalFileName: '',
    };
    const visation = this.formGroup.get('visation')?.value;
    if (visation) {
      visationData.effectiveDate = visation.effectiveDate;
      visationData.observation = visation.visationActions?.observation || ''; // Obtener la observación si existe
      visationData.originalFileName = visation.visationActions?.originalFileName || ''; // Obtener el nombre del archivo original si existe
    }
    const config = {
      title: 'Notificar Cambio',
      description: 'Completa los siguientes campos para notificar el cambio realizado.',
      cancelButtonText: 'Cancelar',
      submitButtonText: 'Enviar',
      fields: [
        {
          name: 'effectiveDate',
          label: 'Indica la fecha que iniciará este cambio',
          type: 'datepicker',
          value: visationData.effectiveDate,
          required: true,
        },
        {
          name: 'comment',
          label: 'Escribe un comentario para la notificación',
          type: 'textarea',
          value: visationData.observation,
          required: true,
        },
        {
          name: 'file',
          label: 'Adjunta documentación',
          type: 'file',
          originalFileName: visationData.originalFileName,
        },
      ],
    };

    const dialogRef = this.dialog.open(VisationModalComponent, {
      width: '400px',
      data: config,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Guardar el archivo en Base64 y su nombre original si existe
        const formData: any = {
          effectiveDate: result.effectiveDate,
          visationActions: {
            observation: result.comment || '', // Asegúrate de que el comentario esté presente
            originalFileName: result.fileName || '', // Controla que no sea undefined
            filePath: result.fileBase64 || '', // Controla que no sea undefined
          },
        };

        console.log('formData: ', formData);

        this.formGroup.patchValue({ visation: formData });
      }
    });
  }
}
